//* Libraries imports
import { motion } from 'framer-motion';
import { Container, Row } from 'react-bootstrap';

//? Styles imports
import './essay-view-skeleton.scss';

interface EssayViewSkeletonProps {
  showSidebar?: boolean;
}

/**
 * EssayViewSkeleton Component
 * 
 * Provides a skeleton loading state for the EssayView component.
 * Mimics the layout structure of the actual essay view with animated placeholders.
 * 
 * @param showSidebar - Whether to show the feedback sidebar skeleton
 */
export function EssayViewSkeleton({ showSidebar = true }: EssayViewSkeletonProps) {
  const skeletonVariants = {
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        repeat: Infinity
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <motion.div
      className="essay-view-skeleton min-vh-100 bg-light"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="d-flex flex-column h-100">
        {/* Header Skeleton */}
        <motion.section 
          className="pt-4 p-2 skeleton-header-section"
          variants={itemVariants}
        >
          <div className="d-flex justify-content-between align-items-center mb-4 skeleton-header">
            <div className="d-flex align-items-center">
              {/* Back Button Skeleton */}
              <motion.div
                className="skeleton-back-button me-3"
                variants={skeletonVariants}
                animate="pulse"
              />
              <div>
                {/* Process Title Skeleton */}
                <motion.div
                  className="skeleton-process-title mb-2"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                {/* Process Info Skeleton */}
                <motion.div
                  className="skeleton-process-info"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>
            </div>
            {/* Status Badge Skeleton */}
            <motion.div
              className="skeleton-status-badge"
              variants={skeletonVariants}
              animate="pulse"
            />
          </div>
        </motion.section>

        {/* Main Content Area */}
        <Container fluid className="flex-grow-1 py-4">
          <Row className="justify-content-center position-relative">
            {/* Essay Viewer Column */}
            <motion.div
              className={`${showSidebar ? 'col-xxl-8 col-xl-8 col-lg-7 col-md-7' : 'col-xxl-11 col-xl-11 col-lg-11 col-md-12'} col-sm-12 essay-viewer-skeleton-col pe-0`}
              variants={itemVariants}
            >
              {/* Essay Content Skeleton */}
              <div className="skeleton-essay-content">
                {/* Essay Header */}
                <motion.div 
                  className="skeleton-essay-header mb-4"
                  variants={itemVariants}
                >
                  <motion.div
                    className="skeleton-essay-title mb-3"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                  <motion.div
                    className="skeleton-essay-prompt mb-2"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                  <motion.div
                    className="skeleton-essay-prompt-line"
                    variants={skeletonVariants}
                    animate="pulse"
                  />
                </motion.div>

                {/* Essay Body Lines */}
                <motion.div 
                  className="skeleton-essay-body"
                  variants={itemVariants}
                >
                  {Array.from({ length: 25 }).map((_, lineIndex) => (
                    <motion.div
                      key={lineIndex}
                      className={`skeleton-essay-line ${
                        lineIndex % 8 === 7 ? 'short' : 
                        lineIndex % 12 === 11 ? 'medium' : 'full'
                      }`}
                      variants={skeletonVariants}
                      animate="pulse"
                      style={{
                        animationDelay: `${lineIndex * 0.05}s`
                      }}
                    />
                  ))}
                </motion.div>

                {/* Essay Footer Info */}
                <motion.div 
                  className="skeleton-essay-footer mt-4"
                  variants={itemVariants}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <motion.div
                      className="skeleton-word-count"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-submission-date"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Sidebar Skeleton */}
            {showSidebar && (
              <motion.div
                className="col-xxl-4 col-xl-4 col-lg-5 col-md-5 col-sm-12 essay-feedback-skeleton-col ps-3 pt-4"
                variants={itemVariants}
              >
                <div className="skeleton-feedback-panel">
                  {/* Feedback Header */}
                  <motion.div 
                    className="skeleton-feedback-header mb-3"
                    variants={itemVariants}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <motion.div
                        className="skeleton-feedback-title"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                      <motion.div
                        className="skeleton-close-button"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                    </div>
                  </motion.div>

                  {/* Feedback Items */}
                  <div className="skeleton-feedback-content">
                    {Array.from({ length: 3 }).map((_, feedbackIndex) => (
                      <motion.div
                        key={feedbackIndex}
                        className="skeleton-feedback-item mb-3"
                        variants={itemVariants}
                      >
                        {/* Feedback Item Header */}
                        <div className="skeleton-feedback-item-header mb-2">
                          <motion.div
                            className="skeleton-feedback-number"
                            variants={skeletonVariants}
                            animate="pulse"
                          />
                          <motion.div
                            className="skeleton-feedback-type"
                            variants={skeletonVariants}
                            animate="pulse"
                          />
                        </div>
                        
                        {/* Feedback Item Content */}
                        <motion.div
                          className="skeleton-feedback-text mb-2"
                          variants={skeletonVariants}
                          animate="pulse"
                        />
                        <motion.div
                          className="skeleton-feedback-text-short"
                          variants={skeletonVariants}
                          animate="pulse"
                        />
                      </motion.div>
                    ))}
                  </div>

                  {/* Correction Summary */}
                  <motion.div 
                    className="skeleton-correction-summary mt-4"
                    variants={itemVariants}
                  >
                    <motion.div
                      className="skeleton-summary-title mb-3"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <div className="skeleton-score-grid">
                      {Array.from({ length: 5 }).map((_, scoreIndex) => (
                        <div key={scoreIndex} className="skeleton-score-item">
                          <motion.div
                            className="skeleton-score-label"
                            variants={skeletonVariants}
                            animate="pulse"
                          />
                          <motion.div
                            className="skeleton-score-value"
                            variants={skeletonVariants}
                            animate="pulse"
                          />
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </Row>
        </Container>
      </div>
    </motion.div>
  );
}
